import { createService } from "./createService"
import { SERVICE_INSTANCES } from "./serviceInstances"

/**
 * Legacy-path performance endpoints served by the new-arch backend
 * (Core.Backend.Api → Admin/PerformanceController, Employee/PerformanceController,
 * PerformanceController). Request models mirror Core.Backend.Domain/Models/PerformanceModel.cs.
 *
 * Every response is wrapped in `PerformanceResponse<T>`. Only the five
 * objective/key-result selects return the `docs` paginator
 * (`PerformancePaginated<TRow>`); every other `select*` route still returns the
 * datatables shape (`PerformanceTable<TRow>`). Errors come back as HTTP 4xx/5xx
 * and are handled by `onQueryError`.
 */

const service = createService(SERVICE_INSTANCES.performance)

export const PERFORMANCE_INSTANCE = service.instanceKey

// ── Response envelopes ───────────────────────────────────────────────────────

/** New-arch envelope — alias of the global `IBaseRes<T>` (src/global.d.ts). */
export type PerformanceResponse<T = any> = IBaseRes<T>

/** `docs` paginator payload — alias of the global `IPaginatedPayload<T>`. */
export type PerformancePaginated<TRow = any> = IPaginatedPayload<TRow>

export type PerformanceListResponse<TRow = any> = IPaginatedRes<TRow>

/** Legacy datatables shape still returned by most `select*` routes (`TableResponseModel`). */
export interface PerformanceTable<TRow = any> {
  total: number
  filtered: number
  data: TRow[]
}

export type PerformanceTableResponse<TRow = any> = PerformanceResponse<
  PerformanceTable<TRow>
>

export interface PaginationParams {
  pageNumber?: number
  pageSize?: number
  search?: string
}

type Id = string | number

// ── Request models (Core.Backend.Domain/Models/PerformanceModel.cs) ─────────

export interface NewPeriodBody {
  perspective_1?: string
  perspective_1_value?: string
  perspective_2?: string
  perspective_2_value?: string
  perspective_3?: string
  perspective_3_value?: string
  perspective_4?: string
  perspective_4_value?: string
  perspective_5?: string
  perspective_5_value?: string
  start_date?: string
  description?: string
  period_type_id?: string
}

export interface PerspectiveUpdateBody {
  description?: string
  alias?: string
}

export interface NewGoalBody {
  period_id?: string
  perspective_id?: string
  description?: string
  start_date?: string
  due_date?: string
}

export interface NewObjectiveBody {
  objective_type_id?: string
  objective_class_id?: string
  department_id?: string
  objective_group_id?: string
  owner_id?: string
  goal_id?: string
  description?: string
  start_date?: string
  due_date?: string
}

export interface NewObjectiveGroupBody {
  description?: string
  alias?: string
  person_list?: string[]
}

export interface NewKeyResultBody {
  objective_id?: string
  description?: string
  start_date?: string
  due_date?: string
  measurement_unit_id?: string
  weight?: string
  currency_code?: string
  is_locked?: string
  target?: string
  person_id?: string
  person_target?: string
  person_start_date?: string
  person_due_date?: string
  person_is_locked?: string
}

export interface NewEmployeeObjectiveBody {
  person_id?: string
  person_target?: string
  person_start_date?: string
  person_due_date?: string
  person_is_locked?: string
}

export interface NewKeyResultItemBody {
  key_result_id?: string
  key_result_item_type_id?: string
  key_result_item_status_id?: string
  start_date?: string
  due_date?: string
  description?: string
}

export interface KeyResultReviewBody {
  id?: string
}

/** Multipart form: `id`, `progress`, `comment`, `file_upload` */
export interface EmployeeObjectiveProgressForm {
  id: Id
  progress?: string
  comment?: string
  file_upload?: File | Blob | null
}

export interface NewRatingBandBody {
  description?: string
  alias?: string
  lower?: string
  upper?: string
}

export interface RatingConfigBody {
  performance?: string
  performance_average?: string
  self?: string
  self_average?: string
  others?: string
  others_average?: string
}

export interface NewCoreValueBody {
  description?: string
  alias?: string
}

export interface NewConversationPromptBody {
  description?: string
  alias?: string
  conversation_type_id?: string
}

export interface NewConversationBody {
  conversation_type_id?: string
  conversation_date?: string
  conversation_time?: string
  conversation_duration_type?: string
  conversation_duration?: string
  conversation_frequency?: string
  conversation_repeat_period?: string
  conversation_repeat_interval_id?: string
  conversation_stop_period?: string
  conversation_stop_interval_id?: string
  conversation_person_id?: string
  person_id?: string
}

export interface NewConversationRoundActionBody {
  start_date?: string
  due_date?: string
  description?: string
}

export interface NewConversationRoundNoteBody {
  note?: string
}

export interface NewReviewBody {
  review_id?: string
  period_id?: string
  review_type?: string
  description?: string
  start_date?: string
  due_date?: string
  repeat_period?: string
  repeat_interval_id?: string
  stop_period?: string
  stop_interval_id?: string
  review_template_id?: string
  default_template?: string
  is_performance_only?: string
  is_shared?: string
  include_core_values?: string
}

export interface ReviewUpdateBody {
  review_id?: string
  period_id?: string
  description?: string
  start_date?: string
  due_date?: string
  review_type?: string
  repeat_period?: string
  repeat_interval_id?: string
  stop_period?: string
  stop_interval_id?: string
  due_days?: string
  include_new_hires?: string
  exempt_period?: string
  exempt_interval_id?: string
  schedule_id?: string
  custom_period?: string
  custom_interval_id?: string
  person_id?: string
}

export interface NewReviewTemplateBody {
  review_template_id?: string
  description?: string
  alias?: string
}

export interface NewReviewTemplateQuestionBody {
  review_template_id?: string
  review_template_question_id?: string
  description?: string
  alias?: string
  response_type_id?: string
  response_option?: string
}

export interface NewReviewQuestionBody {
  review_id?: string
  review_question_id?: string
  description?: string
  alias?: string
  response_type_id?: string
  response_option?: string
  assignments?: string
  core_value_id?: string
}

export interface NewReviewEmployeeBody {
  person_id?: string
}

export interface NewEmployeeReviewBody {
  review_entity_id?: string
  person_id?: string
  employee_review_type_id?: string
}

export interface ReviewAssignmentUpdateBody {
  assignments?: string
  persons?: string
}

export interface ReviewGroupEntityUpdateBody {
  review_group_id?: string
  review_entity_id?: string
}

export interface ReviewEntityScoreBody {
  review_entity_score_id?: string
  performance_score?: string
  performance_score_actual?: string
  self_score?: string
  self_score_actual?: string
  others_score?: string
  others_score_actual?: string
  behavioural_score?: string
  behavioural_score_actual?: string
  final_score?: string
}

export interface ReviewEntityFeedbackBody {
  review_entity_id?: string
  review_entity_feedback_type_id?: string
  description?: string
}

export interface ReviewResponseUpdateBody {
  employee_review_id?: string
  review_question_id?: string
  response?: string
  response_type?: string
  type?: string
}

export interface NewFeedbackBody {
  person_id?: string
  feedback_person_id?: string
  feedback_request_id?: string
  feedback_type?: string
  feedback?: string
  feedback_perspective_id?: string
}

export interface FeedbackResponseBody {
  feedback_request_id?: string
  response?: string
}

export interface NewFeedbackRequestRecipient {
  person_id?: string
  email?: string
  [key: string]: any
}

export interface NewFeedbackRequestBody {
  person_id?: string
  employee_scope?: string
  feedback_request?: string
  person_list?: string[]
  recipient_list?: NewFeedbackRequestRecipient[]
}

// ── Endpoints ────────────────────────────────────────────────────────────────

export const PERFORMANCE_ENDPOINTS = {
  // Employee performance — views
  VIEW_INDEX:                     "/employee/performance/view/index",
  VIEW_CONVERSATION:              "/employee/performance/view/conversation",
  // Employee performance — objectives & key results
  SELECT_OBJECTIVES:              "/employee/performance/selectobjectives",
  SELECT_KEY_RESULTS:             "/employee/performance/selectkeyresults",
  SELECT_KR_FOR_REVIEW:           "/employee/performance/selectkeyresultsforreview",
  SELECT_EMPLOYEE_OBJECTIVES:     "/employee/performance/selectemployeeobjectives",
  SELECT_EMPLOYEE_REPORTS_OBJECTIVES: "/employee/performance/selectemployeereportsobjectives",
  SELECT_REVIEW_ROUNDS:           "/employee/performance/selectreviewrounds",
  GET_OBJECTIVE_ITEMS:            "/employee/performance/getemployeeobjectiveitems",
  GET_PERSONS_FOR_KR:             "/employee/performance/getpersonsforkeyresult",
  ADD_KEY_RESULT:                 "/employee/performance/add_key_result",
  EDIT_KEY_RESULT:                "/employee/performance/edit_key_result",
  DELETE_KEY_RESULT:              "/employee/performance/delete_key_result",
  REMOVE_KEY_RESULT:              "/employee/performance/remove_key_result",
  RESTORE_KEY_RESULT:             "/employee/performance/restore_key_result",
  EDIT_KR_ASSIGNMENT:             "/employee/performance/edit_key_result_assignment",
  ADD_KR_ACTION:                  "/employee/performance/add_key_result_action",
  ADD_KR_COMMENT:                 "/employee/performance/add_key_result_comment",
  LOCK_OBJECTIVE:                 "/employee/performance/lock_employeeobjective",
  UNLOCK_OBJECTIVE:               "/employee/performance/unlock_employeeobjective",
  REMOVE_OBJECTIVE:               "/employee/performance/remove_employeeobjective",
  UPDATE_OBJECTIVE_PROGRESS:      "/employee/performance/update_employeeobjective_progress",
  // Employee performance — feedback
  SELECT_RECEIVED_FEEDBACK:       "/employee/performance/selectreceivedfeedbackrequests",
  SELECT_SENT_FEEDBACK:           "/employee/performance/selectsentfeedbackrequests",
  SELECT_RECEIVED_FEEDBACKS:      "/employee/performance/selectreceivedfeedbacks",
  SELECT_SENT_FEEDBACKS:          "/employee/performance/selectsentfeedbacks",
  GET_FEEDBACK_REQUEST:           "/employee/performance/getfeedbackrequest",
  // Employee performance — conversations
  SELECT_CONVERSATIONS:           "/employee/performance/selectconversations",
  SELECT_CONV_ROUNDS:             "/employee/performance/selectconversationrounds",
  ADD_CONVERSATION:               "/employee/performance/addconversation",
  ADD_CONV_ROUND_ACTION:          "/employee/performance/add_conversationround_action",
  EDIT_CONV_ROUND_ACTION:         "/employee/performance/edit_conversationround_action",
  DELETE_CONV_ROUND_ACTION:       "/employee/performance/delete_conversationround_action",
  UPDATE_CONV_ROUND_ACTION:       "/employee/performance/update_conversationround_action",
  ADD_CONV_ROUND_NOTE:            "/employee/performance/add_conversationround_note",
  EDIT_CONV_ROUND_NOTE:           "/employee/performance/edit_conversationround_note",
  DELETE_CONV_ROUND_NOTE:         "/employee/performance/delete_conversationround_note",
  // Shared performance
  GIVE_FEEDBACK:                  "/performance/givefeedback",
  GIVE_FEEDBACK_FROM_REQUEST:     "/performance/givefeedbackfromrequest",
  REQUEST_FEEDBACK:               "/performance/requestfeedback",
  REPLY_FEEDBACK:                 "/performance/reply_feedback",
  RETRACT_FEEDBACK:               "/performance/retract_feedback",
  RETRACT_FEEDBACK_REQUEST:       "/performance/retract_feedbackrequest",
  SAVE_REVIEW_FEEDBACK:           "/performance/save_review_entity_feedback",
  DELETE_REVIEW_FEEDBACK:         "/performance/delete_review_entity_feedback",
  SAVE_REVIEW_RESPONSE:           "/performance/save_review_response",
  GET_PERFORMANCE_DATA:           "/performance/getperformancedata",
  VIEW_REVIEW:                    "/performance/view/review",
  VIEW_FEEDBACK:                  "/performance/view/feedback",
  // Admin performance — views
  ADMIN_VIEW_PERIODS:             "/admin/performance/view/periods",
  ADMIN_VIEW_OBJECTIVE_GROUPS:    "/admin/performance/view/objective_groups",
  ADMIN_VIEW_REVIEW:              "/admin/performance/view/review",
  ADMIN_VIEW_REVIEWS:             "/admin/performance/view/reviews",
  // Admin performance — selects & lookups (GET)
  ADMIN_SELECT_OBJECTIVES:        "/admin/performance/selectobjectives",
  ADMIN_SELECT_KEY_RESULTS:       "/admin/performance/selectkeyresults",
  ADMIN_SELECT_GOALS:             "/admin/performance/selectgoals",
  ADMIN_SELECT_OBJECTIVE_GROUPS:  "/admin/performance/selectobjectivegroups",
  ADMIN_SELECT_EMPLOYEE_OBJECTIVES: "/admin/performance/selectemployeeobjectives",
  ADMIN_GET_OBJECTIVE:            "/admin/performance/getobjective",
  ADMIN_GET_KEY_RESULT:           "/admin/performance/getkeyresult",
  ADMIN_GET_KEY_RESULT_ITEMS:     "/admin/performance/getkeyresultitems",
  ADMIN_GET_OBJECTIVE_ITEMS:      "/admin/performance/getemployeeobjectiveitems",
  ADMIN_GET_PERIODS:              "/admin/performance/getperiods",
  ADMIN_GET_MEASUREMENT_UNITS:    "/admin/performance/getmeasurementunits",
  ADMIN_GET_OWNERS:               "/admin/performance/getownersforobjective",
  ADMIN_GET_DEPT_OWNERS:          "/admin/performance/getownersfordepartmentalobjective",
  ADMIN_GET_PERSONS_FOR_KR:       "/admin/performance/getpersonsforkeyresult",
  // Admin performance — objectives
  ADMIN_ADD_OBJECTIVE:            "/admin/performance/add_objective",
  ADMIN_EDIT_OBJECTIVE:           "/admin/performance/edit_objective",
  ADMIN_DELETE_OBJECTIVE:         "/admin/performance/delete_objective",
  ADMIN_REMOVE_OBJECTIVE:         "/admin/performance/remove_objective",
  ADMIN_RESTORE_OBJECTIVE:        "/admin/performance/restore_objective",
  // Admin performance — key results
  ADMIN_ADD_KEY_RESULT:           "/admin/performance/add_key_result",
  ADMIN_EDIT_KEY_RESULT:          "/admin/performance/edit_key_result",
  ADMIN_DELETE_KEY_RESULT:        "/admin/performance/delete_key_result",
  ADMIN_REMOVE_KEY_RESULT:        "/admin/performance/remove_key_result",
  ADMIN_RESTORE_KEY_RESULT:       "/admin/performance/restore_key_result",
  ADMIN_LOCK_KEY_RESULT:          "/admin/performance/lock_key_result",
  ADMIN_UNLOCK_KEY_RESULT:        "/admin/performance/unlock_key_result",
  ADMIN_EDIT_KR_ASSIGNMENT:       "/admin/performance/edit_key_result_assignment",
  ADMIN_ADD_KR_COMMENT:           "/admin/performance/add_key_result_comment",
  ADMIN_ADD_KR_ACTION:            "/admin/performance/add_key_result_action",
  ADMIN_UPDATE_KR_ACTION:         "/admin/performance/update_key_result_action",
  ADMIN_UPDATE_OBJECTIVE_PROGRESS: "/admin/performance/update_employeeobjective_progress",
  // Admin performance — goals
  ADMIN_ADD_GOAL:                 "/admin/performance/add_goal",
  ADMIN_EDIT_GOAL:                "/admin/performance/edit_goal",
  ADMIN_DELETE_GOAL:              "/admin/performance/delete_goal",
  ADMIN_REMOVE_GOAL:              "/admin/performance/remove_goal",
  ADMIN_RESTORE_GOAL:             "/admin/performance/restore_goal",
  ADMIN_CLOSE_GOAL:               "/admin/performance/close_goal",
  ADMIN_OPEN_GOAL:                "/admin/performance/open_goal",
  // Admin performance — periods
  ADMIN_ADD_PERIOD:               "/admin/performance/add_period",
  ADMIN_EDIT_PERIOD:              "/admin/performance/edit_period",
  ADMIN_DELETE_PERIOD:            "/admin/performance/delete_period",
  ADMIN_REMOVE_PERIOD:            "/admin/performance/remove_period",
  ADMIN_RESTORE_PERIOD:           "/admin/performance/restore_period",
  ADMIN_RENAME_PERSPECTIVE:       "/admin/performance/rename_perspective",
  ADMIN_GET_PERSPECTIVES:         "/admin/performance/getperspectives",
  // Admin performance — groups
  ADMIN_ADD_OBJECTIVE_GROUP:      "/admin/performance/add_objective_group",
  ADMIN_UPDATE_OBJECTIVE_GROUP:   "/admin/performance/update_objective_group",
  ADMIN_DELETE_OBJECTIVE_GROUP:   "/admin/performance/delete_objective_group",
  // Admin performance — ratings
  ADMIN_GET_RATING_CONFIG:        "/admin/performance/getratingconfiguration",
  ADMIN_EDIT_RATING_CONFIG:       "/admin/performance/editratingconfiguration",
  ADMIN_GET_RATING_BANDS:         "/admin/performance/getratingbands",
  ADMIN_ADD_RATING_BAND:          "/admin/performance/add_rating_band",
  ADMIN_EDIT_RATING_BAND:         "/admin/performance/edit_rating_band",
  ADMIN_DELETE_RATING_BAND:       "/admin/performance/delete_rating_band",
  // Admin performance — conversations
  ADMIN_SELECT_CONVERSATIONS:     "/admin/performance/selectconversations",
  ADMIN_SELECT_CONV_ROUNDS:       "/admin/performance/selectconversationrounds",
  ADMIN_SELECT_CONV_PROMPTS:      "/admin/performance/selectconversationprompts",
  ADMIN_GET_CONV_TYPES:           "/admin/performance/getconversationtypes",
  ADMIN_DELETE_CONVERSATION:      "/admin/performance/delete_conversation",
  ADMIN_REMOVE_CONVERSATION:      "/admin/performance/remove_conversation",
  ADMIN_RESTORE_CONVERSATION:     "/admin/performance/restore_conversation",
  ADMIN_ADD_CONV_PROMPT:          "/admin/performance/add_conversation_prompt",
  ADMIN_UPDATE_CONV_PROMPT:       "/admin/performance/update_conversation_prompt",
  ADMIN_DELETE_CONV_PROMPT:       "/admin/performance/delete_conversation_prompt",
  // Admin performance — objectives (extra)
  ADMIN_REVIEW_KEY_RESULT:        "/admin/performance/review_key_result",
  ADMIN_SELECT_KR_FOR_REVIEW:     "/admin/performance/selectkeyresultsforreview",
  ADMIN_SELECT_ANOTHER_EMP_OBJ:   "/admin/performance/selectanotheremployeeobjectives",
  // Admin performance — feedback
  ADMIN_SELECT_EMPLOYEE_FEEDBACKS: "/admin/performance/selectemployeefeedbacks",
  ADMIN_SELECT_RECEIVED_FEEDBACKS: "/admin/performance/selectreceivedfeedbacks",
  ADMIN_SELECT_SENT_FEEDBACKS:    "/admin/performance/selectsentfeedbacks",
  // Admin performance — reviews
  ADMIN_ADD_REVIEW:               "/admin/performance/add_review",
  ADMIN_DELETE_REVIEW:            "/admin/performance/delete_review",
  ADMIN_REMOVE_REVIEW:            "/admin/performance/remove_review",
  ADMIN_RESTORE_REVIEW:           "/admin/performance/restore_review",
  ADMIN_ADD_REVIEWER:             "/admin/performance/add_reviewer",
  ADMIN_REMOVE_REVIEWER:          "/admin/performance/remove_reviewer",
  ADMIN_SELECT_REVIEW_ROUNDS:     "/admin/performance/selectreviewrounds",
  ADMIN_SELECT_REVIEW_REVIEWEES:  "/admin/performance/selectreviewroundreviewees",
  ADMIN_SELECT_REVIEW_REVIEWERS:  "/admin/performance/selectreviewroundreviewers",
  ADMIN_SELECT_REVIEW_SCORES:     "/admin/performance/selectreviewroundscores",
  ADMIN_SELECT_EMP_REVIEWS:       "/admin/performance/selectemployeereviews",
  ADMIN_SELECT_REVIEW_SETTINGS:   "/admin/performance/selectreviewsettings",
  ADMIN_UPDATE_REVIEW_SETTINGS:   "/admin/performance/update_review_settings",
  ADMIN_ADD_REVIEW_TEMPLATE:      "/admin/performance/add_reviewtemplate",
  ADMIN_REMOVE_REVIEW_TEMPLATE:   "/admin/performance/remove_reviewtemplate",
  ADMIN_CHECK_REVIEW_TEMPLATE:    "/admin/performance/checkreviewtemplate",
  ADMIN_ADD_TEMPLATE_QUESTION:    "/admin/performance/add_reviewtemplate_question",
  ADMIN_EDIT_TEMPLATE_QUESTION:   "/admin/performance/edit_reviewtemplate_question",
  ADMIN_DELETE_TEMPLATE_QUESTION: "/admin/performance/delete_reviewtemplate_question",
  ADMIN_ADD_REVIEW_QUESTION:      "/admin/performance/add_review_question",
  ADMIN_EDIT_REVIEW_QUESTION:     "/admin/performance/edit_review_question",
  ADMIN_DELETE_REVIEW_QUESTION:   "/admin/performance/delete_review_question",
  ADMIN_ADD_REVIEW_EMPLOYEE:      "/admin/performance/add_review_employee",
  ADMIN_ADD_REVIEW_EMPLOYEES:     "/admin/performance/add_review_employees",
  ADMIN_REMOVE_REVIEW_EMPLOYEE:   "/admin/performance/remove_review_employee",
  ADMIN_REMOVE_REVIEW_EMPLOYEES:  "/admin/performance/remove_review_employees",
  ADMIN_ADD_REVIEW_ASSIGNMENT:    "/admin/performance/add_review_assignment",
  ADMIN_REMOVE_REVIEW_ASSIGNMENT: "/admin/performance/remove_review_assignment",
  ADMIN_ADD_REVIEW_RATING_ASSIGNMENT: "/admin/performance/add_review_rating_assignment",
  ADMIN_REMOVE_REVIEW_RATING_ASSIGNMENT: "/admin/performance/remove_review_rating_assignment",
  ADMIN_CLOSE_REVIEW_GROUP:       "/admin/performance/close_review_group",
  ADMIN_OPEN_REVIEW_GROUP:        "/admin/performance/open_review_group",
  ADMIN_DELETE_REVIEW_GROUP:      "/admin/performance/delete_review_group",
  ADMIN_REMOVE_REVIEW_GROUP:      "/admin/performance/remove_review_group",
  ADMIN_RESTORE_REVIEW_GROUP:     "/admin/performance/restore_review_group",
  ADMIN_REFRESH_REVIEW_GROUP:     "/admin/performance/refresh_review_group",
  ADMIN_CREATE_GROUP_SUMMARY:     "/admin/performance/create_review_group_summary",
  ADMIN_EDIT_REVIEW_ENTITY:       "/admin/performance/edit_review_entity_summary",
  ADMIN_UPDATE_GROUP_ENTITIES:    "/admin/performance/update_review_group_entities",
  ADMIN_REMOVE_GROUP_PERSON:      "/admin/performance/remove_review_group_person",
  ADMIN_UPDATE_EMP_REVIEW:        "/admin/performance/update_employeereview_assignment",
  ADMIN_UPDATE_EMP_REVIEW_RATING: "/admin/performance/update_employeereview_rating_assignment",
  ADMIN_UPDATE_REVIEW_CUSTOM:     "/admin/performance/update_review_customassignment",
  ADMIN_UPDATE_REVIEW_RATING_CUSTOM: "/admin/performance/update_review_rating_customassignment",
  ADMIN_SUBMIT_REVIEW:            "/admin/performance/submit_review",
  ADMIN_FINISH_REVIEW:            "/admin/performance/finish_review",
  ADMIN_SAVE_FEEDBACK_RESPONSE:   "/admin/performance/save_feedback_response",
  // Admin performance — core values
  ADMIN_GET_CORE_VALUES:          "/admin/performance/getcorevalues",
  ADMIN_ADD_CORE_VALUE:           "/admin/performance/add_core_value",
  ADMIN_UPDATE_CORE_VALUE:        "/admin/performance/update_core_value",
  ADMIN_DELETE_CORE_VALUE:        "/admin/performance/delete_core_value",
} as const

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Appends defined params to a URL as an encoded query string (for POSTs with [FromQuery] args). */
const withQuery = (url: string, params: Record<string, any>) => {
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join("&")
  return qs ? `${url}?${qs}` : url
}

const toProgressForm = (form: EmployeeObjectiveProgressForm) => {
  const fd = new FormData()
  fd.append("id", String(form.id))
  fd.append("progress", form.progress ?? "")
  fd.append("comment", form.comment ?? "")
  if (form.file_upload) fd.append("file_upload", form.file_upload)
  return fd
}

const E = PERFORMANCE_ENDPOINTS
type R<T = any> = Promise<PerformanceResponse<T>>
type L<TRow = any> = Promise<PerformanceListResponse<TRow>>
type T<TRow = any> = Promise<PerformanceTableResponse<TRow>>

// ── Service ──────────────────────────────────────────────────────────────────

export const performanceService = {
  instanceKey: service.instanceKey,

  // ── Employee — views ─────────────────────────────────────────────────────
  viewIndex:               (periodId?: Id): R =>
    service.get(E.VIEW_INDEX, { period_id: periodId }),
  viewConversation:        (id: Id): R =>
    service.get(E.VIEW_CONVERSATION, { id }),

  // ── Employee — objectives & key results ──────────────────────────────────
  selectObjectives:        (params: {
    period_id?: Id; type_id?: Id; class_id?: Id; state_id?: Id; status_id?: Id; perspective_id?: Id
  } & PaginationParams): L =>
    service.get(E.SELECT_OBJECTIVES, params),
  selectKeyResults:        (params: {
    period_id?: Id; objective_id?: Id; state_id?: Id; status_id?: Id
  } & PaginationParams): L =>
    service.get(E.SELECT_KEY_RESULTS, params),
  selectKRForReview:       (params: { period_id?: Id; objective_id?: Id; state_id?: Id; status_id?: Id }): T =>
    service.get(E.SELECT_KR_FOR_REVIEW, params),
  selectEmployeeObjectives: (params: {
    period_id?: Id; key_result_id?: Id; type_id?: Id; class_id?: Id; state_id?: Id; status_id?: Id
    has_permission?: string; for_employee?: boolean
  }): T =>
    service.get(E.SELECT_EMPLOYEE_OBJECTIVES, params),
  selectReviewRounds:      (params: { person_id: Id; optionId?: Id; stateId?: Id }): T =>
    service.get(E.SELECT_REVIEW_ROUNDS, params),
  selectEmployeeReportsObjectives: (params: {
    type_id?: Id; class_id?: Id; state_id?: Id; status_id?: Id; report_id?: Id; period_id?: Id; perspective_id?: Id
  }): T =>
    service.get(E.SELECT_EMPLOYEE_REPORTS_OBJECTIVES, params),
  getObjectiveItems:       (employeeObjectiveId: Id): R<any[]> =>
    service.get(E.GET_OBJECTIVE_ITEMS, { employee_objective_id: employeeObjectiveId }),
  getPersonsForKR:         (params: { objective_class_id: Id; department_id?: Id; objective_group_id?: Id }): R<any[]> =>
    service.get(E.GET_PERSONS_FOR_KR, params),
  addKeyResult:            (body: NewKeyResultBody[]): R<string> =>
    service.post(E.ADD_KEY_RESULT, body),
  editKeyResult:           (id: Id, body: NewKeyResultBody[]): R<string> =>
    service.post(withQuery(E.EDIT_KEY_RESULT, { id }), body),
  deleteKeyResult:         (id: Id): R<string> =>
    service.post(withQuery(E.DELETE_KEY_RESULT, { id })),
  removeKeyResult:         (id: Id): R<string> =>
    service.post(withQuery(E.REMOVE_KEY_RESULT, { id })),
  restoreKeyResult:        (id: Id): R<string> =>
    service.post(withQuery(E.RESTORE_KEY_RESULT, { id })),
  editKRAssignment:        (id: Id, body: NewEmployeeObjectiveBody[]): R<string> =>
    service.post(withQuery(E.EDIT_KR_ASSIGNMENT, { id }), body),
  addKRAction:             (id: Id, body: NewKeyResultItemBody): R<string> =>
    service.post(withQuery(E.ADD_KR_ACTION, { id }), body),
  addKRComment:            (id: Id, body: NewKeyResultItemBody): R<string> =>
    service.post(withQuery(E.ADD_KR_COMMENT, { id }), body),
  lockObjective:           (id: Id): R<string> =>
    service.post(withQuery(E.LOCK_OBJECTIVE, { id })),
  unlockObjective:         (id: Id): R<string> =>
    service.post(withQuery(E.UNLOCK_OBJECTIVE, { id })),
  removeObjective:         (id: Id): R<string> =>
    service.post(withQuery(E.REMOVE_OBJECTIVE, { id })),
  updateObjectiveProgress: (form: EmployeeObjectiveProgressForm): R<string> =>
    service.upload(E.UPDATE_OBJECTIVE_PROGRESS, toProgressForm(form)),

  // ── Employee — feedback ──────────────────────────────────────────────────
  selectReceivedFeedbackRequests: (personId: Id): T =>
    service.get(E.SELECT_RECEIVED_FEEDBACK, { person_id: personId }),
  selectSentFeedbackRequests:     (personId: Id): T =>
    service.get(E.SELECT_SENT_FEEDBACK, { person_id: personId }),
  selectReceivedFeedbacks:        (personId: Id): T =>
    service.get(E.SELECT_RECEIVED_FEEDBACKS, { person_id: personId }),
  selectSentFeedbacks:            (personId: Id): T =>
    service.get(E.SELECT_SENT_FEEDBACKS, { person_id: personId }),
  getFeedbackRequest:             (id: Id): R =>
    service.get(E.GET_FEEDBACK_REQUEST, { id }),

  // ── Employee — conversations ─────────────────────────────────────────────
  selectConversations:     (params: { person_id: Id; type_id?: Id }): T =>
    service.get(E.SELECT_CONVERSATIONS, params),
  selectConvRounds:        (params: { person_id: Id; type_id?: Id; status_id?: Id }): T =>
    service.get(E.SELECT_CONV_ROUNDS, params),
  addConversation:         (body: NewConversationBody): R<string> =>
    service.post(E.ADD_CONVERSATION, body),
  addConvRoundAction:      (id: Id, body: NewConversationRoundActionBody): R<string> =>
    service.post(withQuery(E.ADD_CONV_ROUND_ACTION, { id }), body),
  editConvRoundAction:     (id: Id, body: NewConversationRoundActionBody): R<string> =>
    service.post(withQuery(E.EDIT_CONV_ROUND_ACTION, { id }), body),
  deleteConvRoundAction:   (id: Id): R<string> =>
    service.post(withQuery(E.DELETE_CONV_ROUND_ACTION, { id })),
  updateConvRoundAction:   (id: Id, statusId: Id): R<string> =>
    service.post(withQuery(E.UPDATE_CONV_ROUND_ACTION, { id, statusId })),
  addConvRoundNote:        (id: Id, body: NewConversationRoundNoteBody): R<string> =>
    service.post(withQuery(E.ADD_CONV_ROUND_NOTE, { id }), body),
  editConvRoundNote:       (id: Id, body: NewConversationRoundNoteBody): R<string> =>
    service.post(withQuery(E.EDIT_CONV_ROUND_NOTE, { id }), body),
  deleteConvRoundNote:     (id: Id): R<string> =>
    service.post(withQuery(E.DELETE_CONV_ROUND_NOTE, { id })),

  // ── Shared — feedback & reviews ──────────────────────────────────────────
  giveFeedback:            (body: NewFeedbackBody): R<boolean> =>
    service.post(E.GIVE_FEEDBACK, body),
  giveFeedbackFromRequest: (body: FeedbackResponseBody): R<boolean> =>
    service.post(E.GIVE_FEEDBACK_FROM_REQUEST, body),
  requestFeedback:         (body: NewFeedbackRequestBody): R<boolean> =>
    service.post(E.REQUEST_FEEDBACK, body),
  replyFeedback:           (id: Id): R<boolean> =>
    service.post(withQuery(E.REPLY_FEEDBACK, { id })),
  retractFeedback:         (id: Id): R<boolean> =>
    service.post(withQuery(E.RETRACT_FEEDBACK, { id })),
  retractFeedbackRequest:  (id: Id): R<boolean> =>
    service.post(withQuery(E.RETRACT_FEEDBACK_REQUEST, { id })),
  saveReviewFeedback:      (body: ReviewEntityFeedbackBody): R<boolean> =>
    service.post(E.SAVE_REVIEW_FEEDBACK, body),
  deleteReviewFeedback:    (id: Id): R<boolean> =>
    service.post(withQuery(E.DELETE_REVIEW_FEEDBACK, { id })),
  saveReviewResponse:      (body: ReviewResponseUpdateBody): R<boolean> =>
    service.post(E.SAVE_REVIEW_RESPONSE, body),
  getPerformanceData:      (params: { id: Id; x?: string; s?: string; r?: string }): R =>
    service.get(E.GET_PERFORMANCE_DATA, params),
  viewReview:              (params: { id: Id; x: string; s?: string }): R =>
    service.get(E.VIEW_REVIEW, params),
  viewFeedback:            (params: { rId: Id; code: string }): R =>
    service.get(E.VIEW_FEEDBACK, params),

  // ── Admin — views ────────────────────────────────────────────────────────
  adminViewPeriods:        (): R<any[]> =>
    service.get(E.ADMIN_VIEW_PERIODS),
  adminViewObjectiveGroups: (id: Id): R =>
    service.get(E.ADMIN_VIEW_OBJECTIVE_GROUPS, { id }),
  adminViewReview:         (id: Id): R =>
    service.get(E.ADMIN_VIEW_REVIEW, { id }),
  adminViewReviews:        (params?: { id?: string; x?: string }): R =>
    service.get(E.ADMIN_VIEW_REVIEWS, params),

  // ── Admin — selects & lookups ────────────────────────────────────────────
  adminSelectObjectives:   (params: {
    period_id?: Id; goal_id?: Id; type_id?: Id; class_id?: Id; state_id?: Id; status_id?: Id
    department_id?: Id; group_id?: Id; owner_id?: Id; perspective_id?: Id
  } & PaginationParams): L =>
    service.get(E.ADMIN_SELECT_OBJECTIVES, params),
  adminSelectKeyResults:   (params: {
    period_id?: Id; objective_id?: Id; state_id?: Id; status_id?: Id
  } & PaginationParams): L =>
    service.get(E.ADMIN_SELECT_KEY_RESULTS, params),
  adminSelectGoals:        (params: {
    period_id: Id; option_id?: Id; perspective_id?: Id; state_id?: Id; status_id?: Id
  }): T =>
    service.get(E.ADMIN_SELECT_GOALS, params),
  adminSelectObjectiveGroups: (): T =>
    service.get(E.ADMIN_SELECT_OBJECTIVE_GROUPS),
  adminSelectEmployeeObjectives: (params: { period_id?: Id; key_result_id?: Id }): T =>
    service.get(E.ADMIN_SELECT_EMPLOYEE_OBJECTIVES, params),
  adminGetObjective:       (id: Id): R =>
    service.get(E.ADMIN_GET_OBJECTIVE, { id }),
  adminGetKeyResult:       (id: Id): R =>
    service.get(E.ADMIN_GET_KEY_RESULT, { id }),
  adminGetKeyResultItems:  (keyResultId: Id): R<any[]> =>
    service.get(E.ADMIN_GET_KEY_RESULT_ITEMS, { key_result_id: keyResultId }),
  adminGetObjectiveItems:  (employeeObjectiveId: Id): R<any[]> =>
    service.get(E.ADMIN_GET_OBJECTIVE_ITEMS, { employee_objective_id: employeeObjectiveId }),
  adminGetPeriods:         (): R<any[]> =>
    service.get(E.ADMIN_GET_PERIODS),
  adminGetMeasurementUnits: (): R<any[]> =>
    service.get(E.ADMIN_GET_MEASUREMENT_UNITS),
  adminGetOwners:          (): R<any[]> =>
    service.get(E.ADMIN_GET_OWNERS),
  adminGetDeptOwners:      (id: Id): R<any[]> =>
    service.get(E.ADMIN_GET_DEPT_OWNERS, { id }),
  adminGetPersonsForKR:    (params: {
    objective_class_id: Id; person_id?: Id; department_id?: Id; objective_group_id?: Id
  }): R<any[]> =>
    service.get(E.ADMIN_GET_PERSONS_FOR_KR, params),

  // ── Admin — objectives ───────────────────────────────────────────────────
  adminAddObjective:       (body: NewObjectiveBody): R<string> =>
    service.post(E.ADMIN_ADD_OBJECTIVE, body),
  adminEditObjective:      (id: Id, body: NewObjectiveBody): R<string> =>
    service.post(withQuery(E.ADMIN_EDIT_OBJECTIVE, { id }), body),
  adminDeleteObjective:    (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_DELETE_OBJECTIVE, { id })),
  adminRemoveObjective:    (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_REMOVE_OBJECTIVE, { id })),
  adminRestoreObjective:   (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_RESTORE_OBJECTIVE, { id })),

  // ── Admin — key results ──────────────────────────────────────────────────
  adminAddKeyResult:       (body: NewKeyResultBody[]): R<string> =>
    service.post(E.ADMIN_ADD_KEY_RESULT, body),
  adminEditKeyResult:      (id: Id, body: NewKeyResultBody[]): R<string> =>
    service.post(withQuery(E.ADMIN_EDIT_KEY_RESULT, { id }), body),
  adminDeleteKeyResult:    (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_DELETE_KEY_RESULT, { id })),
  adminRemoveKeyResult:    (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_REMOVE_KEY_RESULT, { id })),
  adminRestoreKeyResult:   (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_RESTORE_KEY_RESULT, { id })),
  adminLockKeyResult:      (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_LOCK_KEY_RESULT, { id })),
  adminUnlockKeyResult:    (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_UNLOCK_KEY_RESULT, { id })),
  adminEditKRAssignment:   (id: Id, body: NewEmployeeObjectiveBody[]): R<string> =>
    service.post(withQuery(E.ADMIN_EDIT_KR_ASSIGNMENT, { id }), body),
  adminAddKRComment:       (id: Id, comment: string): R<string> =>
    service.post(withQuery(E.ADMIN_ADD_KR_COMMENT, { id, comment })),
  adminAddKRAction:        (id: Id, action: string, startDate: string, dueDate: string): R<string> =>
    service.post(withQuery(E.ADMIN_ADD_KR_ACTION, { id, action, start_date: startDate, due_date: dueDate })),
  adminUpdateKRAction:     (id: Id, statusId: Id): R<string> =>
    service.post(withQuery(E.ADMIN_UPDATE_KR_ACTION, { id, statusId })),
  adminUpdateObjectiveProgress: (form: EmployeeObjectiveProgressForm): R<string> =>
    service.upload(E.ADMIN_UPDATE_OBJECTIVE_PROGRESS, toProgressForm(form)),

  // ── Admin — goals ────────────────────────────────────────────────────────
  adminAddGoal:            (body: NewGoalBody): R<string> =>
    service.post(E.ADMIN_ADD_GOAL, body),
  adminEditGoal:           (id: Id, body: NewGoalBody): R<string> =>
    service.post(withQuery(E.ADMIN_EDIT_GOAL, { id }), body),
  adminDeleteGoal:         (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_DELETE_GOAL, { id })),
  adminRemoveGoal:         (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_REMOVE_GOAL, { id })),
  adminRestoreGoal:        (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_RESTORE_GOAL, { id })),
  adminCloseGoal:          (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_CLOSE_GOAL, { id })),
  adminOpenGoal:           (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_OPEN_GOAL, { id })),

  // ── Admin — periods ──────────────────────────────────────────────────────
  adminAddPeriod:          (body: NewPeriodBody): R<string> =>
    service.post(E.ADMIN_ADD_PERIOD, body),
  adminEditPeriod:         (id: Id, body: NewPeriodBody): R<string> =>
    service.post(withQuery(E.ADMIN_EDIT_PERIOD, { id }), body),
  adminDeletePeriod:       (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_DELETE_PERIOD, { id })),
  adminRemovePeriod:       (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_REMOVE_PERIOD, { id })),
  adminRestorePeriod:      (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_RESTORE_PERIOD, { id })),
  adminRenamePerspective:  (id: Id, body: PerspectiveUpdateBody): R<string> =>
    service.post(withQuery(E.ADMIN_RENAME_PERSPECTIVE, { id }), body),
  adminGetPerspectives:    (): R<any[]> =>
    service.get(E.ADMIN_GET_PERSPECTIVES),

  // ── Admin — groups ───────────────────────────────────────────────────────
  adminAddObjectiveGroup:  (body: NewObjectiveGroupBody): R<string> =>
    service.post(E.ADMIN_ADD_OBJECTIVE_GROUP, body),
  adminUpdateObjectiveGroup: (id: Id, body: NewObjectiveGroupBody): R<string> =>
    service.post(withQuery(E.ADMIN_UPDATE_OBJECTIVE_GROUP, { id }), body),
  adminDeleteObjectiveGroup: (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_DELETE_OBJECTIVE_GROUP, { id })),

  // ── Admin — ratings ──────────────────────────────────────────────────────
  adminGetRatingConfig:    (): R<any[]> =>
    service.get(E.ADMIN_GET_RATING_CONFIG),
  adminEditRatingConfig:   (body: RatingConfigBody): R<string> =>
    service.post(E.ADMIN_EDIT_RATING_CONFIG, body),
  adminGetRatingBands:     (): R<any[]> =>
    service.post(E.ADMIN_GET_RATING_BANDS),
  adminAddRatingBand:      (body: NewRatingBandBody): R<string> =>
    service.post(E.ADMIN_ADD_RATING_BAND, body),
  adminEditRatingBand:     (id: Id, body: NewRatingBandBody): R<string> =>
    service.post(withQuery(E.ADMIN_EDIT_RATING_BAND, { id }), body),
  adminDeleteRatingBand:   (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_DELETE_RATING_BAND, { id })),

  // ── Admin — conversations ────────────────────────────────────────────────
  adminSelectConversations: (typeId: Id): T =>
    service.get(E.ADMIN_SELECT_CONVERSATIONS, { type_id: typeId }),
  adminSelectConvRounds:   (params: { type_id: Id; status_id?: Id }): T =>
    service.get(E.ADMIN_SELECT_CONV_ROUNDS, params),
  adminSelectConvPrompts:  (promptConversationTypeId: Id): T =>
    service.get(E.ADMIN_SELECT_CONV_PROMPTS, { prompt_conversation_type_id: promptConversationTypeId }),
  adminGetConvTypes:       (): R<any[]> =>
    service.get(E.ADMIN_GET_CONV_TYPES),
  adminDeleteConversation: (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_DELETE_CONVERSATION, { id })),
  adminRemoveConversation: (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_REMOVE_CONVERSATION, { id })),
  adminRestoreConversation: (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_RESTORE_CONVERSATION, { id })),
  adminAddConvPrompt:      (body: NewConversationPromptBody): R<string> =>
    service.post(E.ADMIN_ADD_CONV_PROMPT, body),
  adminUpdateConvPrompt:   (id: Id, body: NewConversationPromptBody): R<string> =>
    service.post(withQuery(E.ADMIN_UPDATE_CONV_PROMPT, { id }), body),
  adminDeleteConvPrompt:   (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_DELETE_CONV_PROMPT, { id })),

  // ── Admin — objectives (extra) ───────────────────────────────────────────
  adminReviewKeyResult:    (action: string, body: KeyResultReviewBody[]): R<string> =>
    service.post(withQuery(E.ADMIN_REVIEW_KEY_RESULT, { action }), body),
  adminSelectKRForReview:  (params: { period_id?: Id; objective_id?: Id; state_id?: Id; status_id?: Id }): T =>
    service.get(E.ADMIN_SELECT_KR_FOR_REVIEW, params),
  adminSelectAnotherEmpObj: (params: {
    person_id: Id; period_id?: Id; key_result_id?: Id; type_id?: Id; class_id?: Id
    state_id?: Id; status_id?: Id; for_employee?: boolean
  } & PaginationParams): L =>
    service.get(E.ADMIN_SELECT_ANOTHER_EMP_OBJ, params),

  // ── Admin — feedback ─────────────────────────────────────────────────────
  adminSelectEmployeeFeedbacks: (params: { key: string; locationId?: Id; department_id?: Id; person_id?: Id }): T =>
    service.get(E.ADMIN_SELECT_EMPLOYEE_FEEDBACKS, params),
  adminSelectReceivedFeedbacks: (personId: Id): T =>
    service.get(E.ADMIN_SELECT_RECEIVED_FEEDBACKS, { person_id: personId }),
  adminSelectSentFeedbacks: (personId: Id): T =>
    service.get(E.ADMIN_SELECT_SENT_FEEDBACKS, { person_id: personId }),

  // ── Admin — reviews ──────────────────────────────────────────────────────
  adminAddReview:          (body: NewReviewBody): R<string> =>
    service.post(E.ADMIN_ADD_REVIEW, body),
  adminDeleteReview:       (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_DELETE_REVIEW, { id })),
  adminRemoveReview:       (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_REMOVE_REVIEW, { id })),
  adminRestoreReview:      (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_RESTORE_REVIEW, { id })),
  adminAddReviewer:        (body: NewEmployeeReviewBody): R<string> =>
    service.post(E.ADMIN_ADD_REVIEWER, body),
  adminRemoveReviewer:     (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_REMOVE_REVIEWER, { id })),
  adminSelectReviewRounds: (params?: { option_id?: Id; state_id?: Id; status_id?: Id }): T =>
    service.get(E.ADMIN_SELECT_REVIEW_ROUNDS, params),
  adminSelectReviewReviewees: (reviewGroupId: Id): T =>
    service.get(E.ADMIN_SELECT_REVIEW_REVIEWEES, { review_group_id: reviewGroupId }),
  adminSelectReviewReviewers: (reviewGroupId: Id): T =>
    service.get(E.ADMIN_SELECT_REVIEW_REVIEWERS, { review_group_id: reviewGroupId }),
  adminSelectReviewScores: (reviewGroupId: Id): T =>
    service.get(E.ADMIN_SELECT_REVIEW_SCORES, { review_group_id: reviewGroupId }),
  adminSelectEmpReviews:   (params: { key: string; person_id: Id; locationId?: Id; department_id?: Id }): T =>
    service.get(E.ADMIN_SELECT_EMP_REVIEWS, params),
  adminSelectReviewSettings: (): T =>
    service.get(E.ADMIN_SELECT_REVIEW_SETTINGS),
  adminUpdateReviewSettings: (id: Id, body: ReviewUpdateBody[]): R<string> =>
    service.post(withQuery(E.ADMIN_UPDATE_REVIEW_SETTINGS, { id }), body),
  adminAddReviewTemplate:  (body: NewReviewTemplateBody): R<string> =>
    service.post(E.ADMIN_ADD_REVIEW_TEMPLATE, body),
  adminRemoveReviewTemplate: (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_REMOVE_REVIEW_TEMPLATE, { id })),
  adminCheckReviewTemplate: (params: { description: string; review_template_id?: Id }): R<string> =>
    service.get(E.ADMIN_CHECK_REVIEW_TEMPLATE, params),
  adminAddTemplateQuestion: (id: Id, body: NewReviewTemplateQuestionBody): R<string> =>
    service.post(withQuery(E.ADMIN_ADD_TEMPLATE_QUESTION, { id }), body),
  adminEditTemplateQuestion: (id: Id, body: NewReviewTemplateQuestionBody): R<string> =>
    service.post(withQuery(E.ADMIN_EDIT_TEMPLATE_QUESTION, { id }), body),
  adminDeleteTemplateQuestion: (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_DELETE_TEMPLATE_QUESTION, { id })),
  adminAddReviewQuestion:  (id: Id, body: NewReviewQuestionBody): R<string> =>
    service.post(withQuery(E.ADMIN_ADD_REVIEW_QUESTION, { id }), body),
  adminEditReviewQuestion: (id: Id, body: NewReviewQuestionBody): R<string> =>
    service.post(withQuery(E.ADMIN_EDIT_REVIEW_QUESTION, { id }), body),
  adminDeleteReviewQuestion: (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_DELETE_REVIEW_QUESTION, { id })),
  adminAddReviewEmployee:  (id: Id, body: NewReviewEmployeeBody): R<string> =>
    service.post(withQuery(E.ADMIN_ADD_REVIEW_EMPLOYEE, { id }), body),
  adminAddReviewEmployees: (id: Id, body: NewReviewEmployeeBody[]): R<string> =>
    service.post(withQuery(E.ADMIN_ADD_REVIEW_EMPLOYEES, { id }), body),
  adminRemoveReviewEmployee: (id: Id, body: NewReviewEmployeeBody): R<string> =>
    service.post(withQuery(E.ADMIN_REMOVE_REVIEW_EMPLOYEE, { id }), body),
  adminRemoveReviewEmployees: (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_REMOVE_REVIEW_EMPLOYEES, { id })),
  adminAddReviewAssignment: (id: Id, role: string): R<string> =>
    service.post(withQuery(E.ADMIN_ADD_REVIEW_ASSIGNMENT, { id, role })),
  adminRemoveReviewAssignment: (id: Id, role: string): R<string> =>
    service.post(withQuery(E.ADMIN_REMOVE_REVIEW_ASSIGNMENT, { id, role })),
  adminAddReviewRatingAssignment: (id: Id, role: string): R<string> =>
    service.post(withQuery(E.ADMIN_ADD_REVIEW_RATING_ASSIGNMENT, { id, role })),
  adminRemoveReviewRatingAssignment: (id: Id, role: string): R<string> =>
    service.post(withQuery(E.ADMIN_REMOVE_REVIEW_RATING_ASSIGNMENT, { id, role })),
  adminCloseReviewGroup:   (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_CLOSE_REVIEW_GROUP, { id })),
  adminOpenReviewGroup:    (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_OPEN_REVIEW_GROUP, { id })),
  adminDeleteReviewGroup:  (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_DELETE_REVIEW_GROUP, { id })),
  adminRemoveReviewGroup:  (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_REMOVE_REVIEW_GROUP, { id })),
  adminRestoreReviewGroup: (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_RESTORE_REVIEW_GROUP, { id })),
  adminRefreshReviewGroup: (id: Id, force?: boolean): R<string> =>
    service.post(withQuery(E.ADMIN_REFRESH_REVIEW_GROUP, { id, force })),
  adminCreateGroupSummary: (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_CREATE_GROUP_SUMMARY, { id })),
  adminEditReviewEntity:   (body: ReviewEntityScoreBody): R<string> =>
    service.post(E.ADMIN_EDIT_REVIEW_ENTITY, body),
  adminUpdateGroupEntities: (body: ReviewGroupEntityUpdateBody): R<string> =>
    service.post(E.ADMIN_UPDATE_GROUP_ENTITIES, body),
  adminRemoveGroupPerson:  (id: Id, body: NewReviewEmployeeBody): R<string> =>
    service.post(withQuery(E.ADMIN_REMOVE_GROUP_PERSON, { id }), body),
  adminUpdateEmpReview:    (id: Id, body: ReviewAssignmentUpdateBody): R<string> =>
    service.post(withQuery(E.ADMIN_UPDATE_EMP_REVIEW, { id }), body),
  adminUpdateEmpReviewRating: (id: Id, body: ReviewAssignmentUpdateBody): R<string> =>
    service.post(withQuery(E.ADMIN_UPDATE_EMP_REVIEW_RATING, { id }), body),
  adminUpdateReviewCustom: (id: Id, body: ReviewAssignmentUpdateBody): R<string> =>
    service.post(withQuery(E.ADMIN_UPDATE_REVIEW_CUSTOM, { id }), body),
  adminUpdateReviewRatingCustom: (id: Id, body: ReviewAssignmentUpdateBody): R<string> =>
    service.post(withQuery(E.ADMIN_UPDATE_REVIEW_RATING_CUSTOM, { id }), body),
  adminSubmitReview:       (employeeReviewId: Id): R<string> =>
    service.post(withQuery(E.ADMIN_SUBMIT_REVIEW, { employee_review_id: employeeReviewId })),
  adminFinishReview:       (body: NewReviewBody): R<string> =>
    service.post(E.ADMIN_FINISH_REVIEW, body),
  adminSaveFeedbackResponse: (body: ReviewResponseUpdateBody): R<string> =>
    service.post(E.ADMIN_SAVE_FEEDBACK_RESPONSE, body),

  // ── Admin — core values ──────────────────────────────────────────────────
  adminGetCoreValues:      (): R<any[]> =>
    service.get(E.ADMIN_GET_CORE_VALUES),
  adminAddCoreValue:       (body: NewCoreValueBody): R<string> =>
    service.post(E.ADMIN_ADD_CORE_VALUE, body),
  adminUpdateCoreValue:    (id: Id, body: NewCoreValueBody): R<string> =>
    service.post(withQuery(E.ADMIN_UPDATE_CORE_VALUE, { id }), body),
  adminDeleteCoreValue:    (id: Id): R<string> =>
    service.post(withQuery(E.ADMIN_DELETE_CORE_VALUE, { id })),
}
